import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiEditorText } from '@wix/da-papi-types';
import EditIcon from '../../Icons/16x16/Edit';
import { IconSize } from '../../Icons/IconWrap';
import Checkbox from '../../Checkbox';
import RadioButton from '../../RadioButton';
import TextInput from '../../formControls/dsPresets/TextInput';
import RequiredMarker from '../RequiredMarker';
import { formatDollars } from '../../../utils/formatting';
import { Question, Order } from '../types';
import InputErrorMessage from '../../formControls/lego/InputErrorMessage';

import s from './QuestionDisplay.scss';

interface Props {
  className?: string;
  question: Question;
  order?: Order;
  editorRenderer: (body: PapiEditorText) => React.ReactNode;
}

const QuestionDisplay: React.FC<Props> = ({
  className,
  question,
  order,
  editorRenderer,
}) => {
  const { t } = useTranslation();
  const orderAnswer = order?.orderAnswers?.find(
    answer => answer.questionId === question.questionId
  );

  return (
    <div className={className}>
      {question.isRequired && <RequiredMarker />}
      <div className={s['question-body']}>
        {editorRenderer(question.question)}
      </div>

      {question.type === 'free_text' &&
        (order && orderAnswer?.answer ? (
          editorRenderer(orderAnswer.answer)
        ) : order ? (
          <InputErrorMessage>
            {t('originals.questionDisplay.noAnswer')}
          </InputErrorMessage>
        ) : (
          <div className={s['free-text-placeholder']}>
            <TextInput
              id="free-text-placeholder"
              label={t(
                'originalDeviationPopup.questionnaire.form.freeText.a11y.label'
              )}
              isLabelHidden
              placeholder={t(
                'originalDeviationPopup.questionnaire.form.freeText.placeholder'
              )}
              prefix={
                <EditIcon
                  className={s['free-text-icon']}
                  size={IconSize.SMALL}
                  disableDefaultColors
                />
              }
              disabled
              noBottomSpacing
            />
          </div>
        ))}

      {['single_choice', 'multiple_choice'].includes(question.type) && (
        <div className={s['answer-options']}>
          {question.answers?.map((choice, index) => (
            <div className={s['answer-option']} key={index}>
              <div className={s['answer-option-input']}>
                {question.type === 'multiple_choice' && (
                  <Checkbox
                    size="small"
                    originalGreen
                    isChecked={
                      (choice.answerId &&
                        orderAnswer?.answerIds?.includes(choice.answerId)) ||
                      false
                    }
                    disabled={!orderAnswer}
                  />
                )}
                {question.type === 'single_choice' && (
                  <RadioButton
                    name="answer-option"
                    value={choice.answer}
                    checked={
                      (choice.answerId &&
                        orderAnswer?.answerIds?.includes(choice.answerId)) ||
                      false
                    }
                    disabled={!orderAnswer}
                  />
                )}
              </div>
              <div className={s['answer-option-text']}>{choice.answer}</div>
              {choice.priceModifier && choice.priceModifier > 0 ? (
                <div className={s['answer-option-price']}>
                  {formatDollars(choice.priceModifier)}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuestionDisplay;
